import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { dayText } from "./accounts";
import * as api from "./api";
import { holidayChange, nameLengthText, WEEKDAYS, weekdayOf } from "./calendar";
import { Skeleton } from "./Skeleton";

/** Spec F-ADM-3: the holidays of the organization. A stop on a holiday moves by the calendar rule. */
export function HolidaySettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.HolidayList | "failed" | undefined>(undefined);
  const [day, setDay] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState<{ ok: boolean; field?: "day" | "name"; text: string } | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const dayInput = useRef<HTMLInputElement>(null);
  const nameInput = useRef<HTMLInputElement>(null);
  const status = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    user
      .getIdToken()
      .then(api.holidays)
      .then(setFound, () => setFound("failed"));
  }, [user]);

  if (found === "failed") {
    return (
      <fieldset>
        <legend>Holidays</legend>
        <p className="error">Cannot load the holidays. Try again later.</p>
      </fieldset>
    );
  }
  if (found === undefined) {
    return (
      <fieldset aria-busy="true">
        <legend>Holidays</legend>
        <Skeleton shape="form" rows={6} label="Loading the holidays" />
      </fieldset>
    );
  }
  const failed = (e: unknown, what: string) => {
    const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
    setMessage({ ok: false, text: detail ?? `The holiday was not ${what}. Try again.` });
  };
  const add = async (event: FormEvent) => {
    event.preventDefault();
    const change = holidayChange(day, name, found.holidays.map((holiday) => holiday.day));
    if ("text" in change) {
      setMessage({ ok: false, field: change.field, text: change.text });
      (change.field === "day" ? dayInput : nameInput).current?.focus();
      return;
    }
    setSaving("new");
    setMessage(null);
    try {
      const next = await api.addHoliday(await user.getIdToken(), { id: crypto.randomUUID(), ...change });
      setFound(next);
      setDay("");
      setName("");
      setMessage({ ok: true, text: `Saved: ${change.name} on ${dayText(change.day, true)}.` });
      requestAnimationFrame(() => status.current?.focus());
    } catch (e) {
      failed(e, "saved");
    } finally {
      setSaving(null);
    }
  };
  const remove = async (holiday: api.Holiday) => {
    setSaving(holiday.id);
    setMessage(null);
    try {
      setFound(await api.removeHoliday(await user.getIdToken(), holiday.id));
      setMessage({ ok: true, text: `Removed: ${holiday.name} on ${dayText(holiday.day, true)}.` });
      requestAnimationFrame(() => status.current?.focus());
    } catch (e) {
      failed(e, "removed");
    } finally {
      setSaving(null);
    }
  };
  const long = nameLengthText(name);
  const wrongField = message && !message.ok ? message.field : undefined;
  const wrong = (field: "day" | "name") => wrongField === field || (field === "name" && Boolean(long));
  const errorText = wrongField ? message?.text : long;
  return (
    <form onSubmit={add} noValidate data-testid="holiday-settings">
      <fieldset disabled={saving !== null}>
        <legend>Holidays</legend>
        {found.holidays.length === 0 ? (
          <p data-testid="holiday-none">No holidays. Stops stay on their days.</p>
        ) : (
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Holidays">
            <table className="list" data-testid="holiday-table">
              <thead>
                <tr>
                  <th scope="col">Day</th>
                  <th scope="col">Name</th>
                  {found.can_edit && <th scope="col">Remove</th>}
                </tr>
              </thead>
              <tbody>
                {found.holidays.map((holiday) => (
                  <tr key={holiday.id} data-testid="holiday-row" data-day={holiday.day}>
                    <td>
                      {WEEKDAYS[weekdayOf(holiday.day)].slice(0, 3)} {dayText(holiday.day, true)}
                    </td>
                    <th scope="row">{holiday.name}</th>
                    {found.can_edit && (
                      <td>
                        <button
                          type="button"
                          className="quiet"
                          data-testid="holiday-remove"
                          aria-label={`Remove ${holiday.name} on ${dayText(holiday.day, true)}`}
                          onClick={() => void remove(holiday)}
                        >
                          {saving === holiday.id ? "Removing…" : "Remove"}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {found.can_edit && (
          <div className="limit">
            <label htmlFor="holiday-day">Day</label>
            <input
              id="holiday-day"
              data-testid="holiday-day"
              type="date"
              ref={dayInput}
              value={day}
              aria-invalid={wrong("day") || undefined}
              aria-describedby={wrong("day") ? "holiday-error holiday-hint" : "holiday-hint"}
              onChange={(e) => {
                setDay(e.target.value);
                setMessage(null);
              }}
            />
            <label htmlFor="holiday-name">Name</label>
            <input
              id="holiday-name"
              data-testid="holiday-name"
              ref={nameInput}
              value={name}
              aria-invalid={wrong("name") || undefined}
              aria-describedby={wrong("name") ? "holiday-error holiday-hint" : "holiday-hint"}
              onChange={(e) => {
                setName(e.target.value);
                setMessage(null);
              }}
            />
            {errorText && (
              <p id="holiday-error" role="alert" className="error" data-testid="holiday-field-error">
                {errorText}
              </p>
            )}
            <p id="holiday-hint" className="hint">
              A stop on a holiday moves by the rule of the cycle calendar. The next visit and the cycle board use the holidays.
            </p>
            <div className="actions">
              <button className="primary" data-testid="holiday-add">
                {saving === "new" ? "Adding…" : "Add the holiday"}
              </button>
            </div>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="holiday-status" className={message && !message.ok ? "error" : undefined}>
        {wrongField ? "" : message?.text}
      </p>
    </form>
  );
}
